<template>
  <div class="p2p-settings-container">
    <!-- P2P客户端设置表格 -->
    <div class="settings-card">
      <div class="card-header">
        <div class="card-header-content">
          <h3 class="card-title">{{ $t('p2pSetting.title') }}</h3>
          <el-button type="info" size="small" @click="showLogDialog">
            <el-icon><Document /></el-icon>
            {{ $t('p2pSetting.viewLogs') }}
          </el-button>
        </div>
      </div>
      <div class="card-body">
        <div class="table-responsive">
          <table class="settings-table">
            <thead>
              <tr>
                <th>{{ $t('p2pSetting.currentCount') }}</th>
                <th>{{ $t('p2pSetting.maxCount') }}</th>
                <th>{{ $t('p2pSetting.operation') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td data-label="当前数量">{{ p2pConfig.currentP2PClientNum }}</td>
                <td data-label="最大数量">
                  <el-input
                    v-model.number="p2pConfigForm.maxP2PClientNum"
                    type="number"
                    min="1"
                    max="1000"
                    placeholder="1-1000"
                  />
                </td>
                <td data-label="操作" class="action-cell">
                  <el-button
                    type="primary"
                    size="small"
                    @click="updateP2PClientService"
                  >
                    {{ $t('p2pSetting.update') }}
                  </el-button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- P2P服务器设置表格 -->
    <div class="settings-card">
      <div class="card-header">
        <h3 class="card-title">{{ $t('p2pSetting.p2pServerSettings') }}</h3>
      </div>
      <div class="card-body">
        <div class="table-responsive">
          <table class="settings-table">
            <thead>
              <tr>
                <th>{{ $t('p2pSetting.port') }}</th>
                <th>{{ $t('p2pSetting.enableRelay') }}</th>
                <th>{{ $t('p2pSetting.status') }}</th>
                <th>{{ $t('p2pSetting.operation') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td data-label="端口号">
                  <el-input
                    v-model.number="p2pServerForm.serverListenPort"
                    type="number"
                    min="1"
                    max="65535"
                    placeholder="1-65535"
                  />
                </td>
                <td data-label="开启中转">
                  <el-select v-model="p2pServerForm.closeTransfer" :placeholder="$t('p2pSetting.selectRelayStatus')">
                    <el-option value="on" :label="$t('p2pSetting.running')" />
                    <el-option value="off" :label="$t('p2pSetting.stopped')" />
                  </el-select>
                </td>
                <td data-label="状态">
                  <span :class="getStatusClass(p2pServer.serverStatus)">
                    {{ p2pServer.serverStatus ? $t('p2pSetting.running') : $t('p2pSetting.stopped') }}
                  </span>
                </td>
                <td data-label="操作" class="action-cell">
                  <div class="action-buttons">
                    <template v-if="p2pServer.serverStatus">
                      <el-button
                        type="primary"
                        size="small"
                        @click="stopP2PServerService"
                      >
                        {{ $t('p2pSetting.stop') }}
                      </el-button>
                      <el-button
                        type="primary"
                        size="small"
                        @click="openP2PListStatus"
                      >
                        {{ $t('p2pSetting.viewConnections') }}
                      </el-button>
                    </template>
                    <el-button
                      v-else
                      type="primary"
                      size="small"
                      @click="updateP2PServerService"
                    >
                      {{ $t('p2pSetting.updateAndStart') }}
                    </el-button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- P2P日志查看对话框 -->
    <el-dialog
      v-model="logDialogVisible"
      :title="$t('p2pSetting.runningLogs')"
      :width="isMobile ? '95%' : '900px'"
      :close-on-click-modal="false"
      :destroy-on-close="true"
      class="settings-dialog log-dialog"
    >
      <div class="log-toolbar">
        <div class="log-stats">
          <span>{{ $t('p2pSetting.totalLogs', { count: logTotal }) }}</span>
          <div class="auto-refresh-control">
            <span class="refresh-label">{{ $t('p2pSetting.autoRefresh') }}</span>
            <el-switch
              v-model="autoRefreshEnabled"
              :active-value="true"
              :inactive-value="false"
              @change="toggleAutoRefresh"
              size="small"
              active-color="#409eff"
            />
            <span class="refresh-interval" v-if="autoRefreshEnabled">{{ $t('p2pSetting.refreshInterval') }}</span>
          </div>
        </div>
        <div class="log-actions">
          <el-button
            type="success"
            size="small"
            @click="downloadAllLogs"
            :loading="logDownloading"
            :icon="Download"
          >
            {{ $t('p2pSetting.downloadAllLogs') }}
          </el-button>
          <el-button
            type="primary"
            size="small"
            @click="refreshLogs"
            :loading="logLoading"
            :icon="Refresh"
          >
            {{ $t('p2pSetting.refreshNow') }}
          </el-button>
          <el-button
            type="danger"
            size="small"
            @click="clearLogs"
            :loading="logClearing"
          >
            {{ $t('p2pSetting.clearLogs') }}
          </el-button>
        </div>
      </div>

      <div class="log-content" ref="logContentRef">
        <div v-if="logsData.length === 0" class="empty-log">
          <el-icon><InfoFilled /></el-icon>
          <p>{{ $t('p2pSetting.noLogs') }}</p>
        </div>
        <div v-else class="log-list">
          <div
            v-for="(log, index) in logsData"
            :key="index"
            class="log-item"
            :class="`log-level-${log.Level.toLowerCase()}`"
          >
            <span class="log-time">{{ log.Time }}</span>
            <span class="log-level" :class="`level-${log.Level.toLowerCase()}`">
              [{{ log.Level }}]
            </span>
            <span class="log-message">{{ log.Content }}</span>
          </div>
        </div>
      </div>

      <div class="log-pagination" v-if="logTotal > 0">
        <el-pagination
          v-model:current-page="logCurrentPage"
          v-model:page-size="logPageSize"
          :page-sizes="[20, 50, 100, 200]"
          :total="logTotal"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleLogSizeChange"
          @current-change="handleLogPageChange"
          small
        />
      </div>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="logDialogVisible = false">{{ $t('p2pSetting.close') }}</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Document, InfoFilled } from '@element-plus/icons-vue'
import { API_BASE_URL, HOME_BASE_URL } from "@/config";
import { useRouter } from 'vue-router'

export default {
  name: 'P2PSettings',
  components: { Document, InfoFilled },
  setup() {
    const { t: i18n_t } = useI18n()
    const router = useRouter()
    const isMobile = ref(false)
    
    // P2P配置数据
    const p2pConfig = ref({
      currentP2PClientNum: 0,
      maxP2PClientNum: 0
    })
    
    const p2pConfigForm = ref({
      maxP2PClientNum: 0
    })
    
    // P2P服务器数据
    const p2pServer = ref({
      serverListenPort: 0,
      serverStatus: false,
      closeTransfer: false,
      status: ''
    })
    
    const p2pServerForm = ref({
      serverListenPort: 0,
      closeTransfer: 'off'
    })

    // 日志相关
    const logDialogVisible = ref(false)
    const logLoading = ref(false)
    const logClearing = ref(false)
    const logDownloading = ref(false)
    const logsData = ref([])
    const logTotal = ref(0)
    const logCurrentPage = ref(1)
    const logPageSize = ref(50)
    const logContentRef = ref(null)
    const autoRefreshEnabled = ref(true)
    const logRefreshInterval = ref(null)
    
    // 检测移动端
    const checkMobile = () => {
      isMobile.value = window.innerWidth < 768
    }
    
    // 获取状态样式类
    const getStatusClass = (status) => {
      if (status === true || status === 'true' || status === i18n_t('p2pSetting.running')) {
        return 'status running'
      } else if (status === false || status === 'false' || status === i18n_t('p2pSetting.stopped')) {
        return 'status stopped'
      } else {
        const lowerStatus = String(status).toLowerCase()
        if (lowerStatus.includes('failed') || lowerStatus.includes('失败')) {
          return 'status failed'
        } else if (lowerStatus.includes('success') || lowerStatus.includes('running') || lowerStatus.includes('运行中')) {
          return 'status running'
        } else {
          return 'status stopped'
        }
      }
    }

    // 获取P2P配置数据
    const fetchP2PConfig = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/get-p2pConfig`, {
          method: 'GET',
          credentials: 'include'
        })

        if (!response.ok) {
          throw new Error(i18n_t('p2pSetting.fetchConfigFailed'))
        }

        const result = await response.json()

        if (result.errCode === 1000) {
          ElMessage.error(i18n_t('p2pSetting.pleaseLoginFirst'))
          router.push('/user-login?callback=p2p-setting')
          return
        }

        if (result.errCode != 0) {
          ElMessage.error(result.errMsg)
          return
        }

        p2pConfig.value = {
          currentP2PClientNum: result.currentP2PClientNum || 0,
          maxP2PClientNum: result.maxP2PClientNum || 0
        }

        p2pConfigForm.value.maxP2PClientNum = result.maxP2PClientNum || 0

        p2pServer.value = {
          serverListenPort: result.p2pServer?.serverListenPort || 0,
          serverStatus: result.p2pServer?.serverStatus || false,
          closeTransfer: result.p2pServer?.closeTransfer || false,
          status: result.p2pServer?.status || ''
        }

        p2pServerForm.value = {
          serverListenPort: result.p2pServer?.serverListenPort || 0,
          closeTransfer: result.p2pServer?.closeTransfer ? 'on' : 'off'
        }

      } catch (error) {
        console.error(i18n_t('p2pSetting.fetchConfigFailed') + ':', error)
        ElMessage.error(error.message || i18n_t('p2pSetting.fetchConfigFailed'))
      }
    }

    // 更新P2P客户端数量
    const updateP2PClientService = async () => {
      try {
        if (!p2pConfigForm.value.maxP2PClientNum) {
          ElMessage.error(i18n_t('p2pSetting.countRequired'))
          return
        }

        if (p2pConfigForm.value.maxP2PClientNum < 1 || p2pConfigForm.value.maxP2PClientNum > 1000) {
          ElMessage.error(i18n_t('p2pSetting.countRangeError'))
          return
        }

        const params = new URLSearchParams()
        params.append('maxP2PClientNum', p2pConfigForm.value.maxP2PClientNum)

        const response = await fetch(`${API_BASE_URL}/update-p2pClientNum`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          credentials: 'include',
          body: params
        })

        if (!response.ok) {
          throw new Error(i18n_t('p2pSetting.updateFailed'))
        }

        const data = await response.json()

        if (data.errMsg === "success") {
          ElMessageBox.confirm(i18n_t('p2pSetting.updateSuccess'), i18n_t('p2pSetting.confirm'), {
            confirmButtonText: i18n_t('p2pSetting.confirm'),
            cancelButtonText: i18n_t('p2pSetting.cancelText'),
            type: 'success'
          }).then(() => {
            restartSoftware()
          }).catch(() => {
            fetchP2PConfig()
          })
        } else {
          ElMessage.error(data.errMsg || i18n_t('p2pSetting.updateFailed'))
          fetchP2PConfig()
        }
      } catch (error) {
        ElMessage.error(error.message || i18n_t('p2pSetting.updateFailed'))
      }
    }

    // 更新P2P服务器设置
    const updateP2PServerService = async () => {
      try {
        if (!p2pServerForm.value.serverListenPort) {
          ElMessage.error(i18n_t('p2pSetting.portRequired'))
          return
        }

        if (p2pServerForm.value.serverListenPort < 1 || p2pServerForm.value.serverListenPort > 65535) {
          ElMessage.error(i18n_t('p2pSetting.portRangeError'))
          return
        }

        if (!p2pServerForm.value.closeTransfer) {
          ElMessage.error(i18n_t('p2pSetting.selectRelayStatus'))
          return
        }

        const params = new URLSearchParams()
        params.append('lport', p2pServerForm.value.serverListenPort)
        params.append('closeTransfer', p2pServerForm.value.closeTransfer)

        const response = await fetch(`${API_BASE_URL}/start-p2pServer`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          credentials: 'include',
          body: params
        })

        if (!response.ok) {
          throw new Error(i18n_t('p2pSetting.updateFailed'))
        }

        const data = await response.json()
        ElMessage.success(data.errMsg || i18n_t('p2pSetting.operationSuccess'))
        fetchP2PConfig()
      } catch (error) {
        ElMessage.error(error.message || i18n_t('p2pSetting.updateFailed'))
      }
    }

    // 停止P2P服务器
    const stopP2PServerService = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/stop-p2pServer`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          credentials: 'include'
        })

        if (!response.ok) {
          throw new Error(i18n_t('p2pSetting.stopFailed'))
        }

        const data = await response.json()
        ElMessage.success(data.errMsg || i18n_t('p2pSetting.stopSuccess'))
        fetchP2PConfig()
      } catch (error) {
        ElMessage.error(error.message || i18n_t('p2pSetting.stopFailed'))
      }
    }

    // 查看连接状态
    const openP2PListStatus = () => {
      window.open(`${HOME_BASE_URL}/get-p2pNode/`, '_blank')
    }

    const restartSoftware = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/restart-proccess`, {
          credentials: 'include'
        });

        const data = await response.json();

        if (data.errCode !== 0) {
          throw new Error(data.errMsg || i18n_t('p2pSetting.restartSoftwareFailed'));
        }

        ElMessage.success(i18n_t('p2pSetting.softwareWillRestart'));
        setTimeout(() => {
          window.location.reload();
        }, 3000);
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') {
          console.error(i18n_t('p2pSetting.restartSoftwareFailed') + ':', error);
          ElMessage.error(i18n_t('p2pSetting.connectionLost') + ': ' + error.message);
        }
      }
    };

    // 显示日志对话框
    const showLogDialog = () => {
      logDialogVisible.value = true
      fetchLogs()
      // 开启日志自动刷新（如果开启状态）
      if (autoRefreshEnabled.value) {
        startLogAutoRefresh()
      }
    }

    // 切换自动刷新
    const toggleAutoRefresh = (enabled) => {
      if (enabled) {
        startLogAutoRefresh()
        ElMessage.success(i18n_t('p2pSetting.autoRefreshEnabled'))
      } else {
        stopLogAutoRefresh()
        ElMessage.info(i18n_t('p2pSetting.autoRefreshDisabled'))
      }
    }

    // 启动日志自动刷新
    const startLogAutoRefresh = () => {
      stopLogAutoRefresh() // 先停止已有的定时器
      logRefreshInterval.value = setInterval(() => {
        if (logDialogVisible.value) {
          fetchLogs(false) // 静默刷新，不显示加载状态
        }
      }, 5000)
    }

    // 停止日志自动刷新
    const stopLogAutoRefresh = () => {
      if (logRefreshInterval.value) {
        clearInterval(logRefreshInterval.value)
        logRefreshInterval.value = null
      }
    }

    // 获取日志
    const fetchLogs = async (showLoading = true) => {
      try {
        if (showLoading) {
          logLoading.value = true
        }

        const params = new URLSearchParams({
          module: 'p2p',
          page: logCurrentPage.value,
          size: logPageSize.value
        })

        const response = await fetch(`${API_BASE_URL}/get-moduleLog?${params.toString()}`, {
          credentials: 'include'
        })

        if (!response.ok) throw new Error(i18n_t('p2pSetting.networkError'))

        const data = await response.json()

        if (data.errCode === 1000) {
          ElMessage.error(i18n_t('p2pSetting.pleaseLoginFirst'))
          router.push('/user-login?callback=p2p-setting')
          return
        }

        if (data.errCode !== 0) {
          throw new Error(data.errMsg || i18n_t('p2pSetting.fetchLogsFailed'))
        }

        logsData.value = data.data || []
        logTotal.value = data.count || 0

        // 只有在非自动刷新（即手动操作）时才滚动到顶部
        if (showLoading && logContentRef.value) {
          logContentRef.value.scrollTop = 0
        }
      } catch (error) {
        console.error(i18n_t('p2pSetting.fetchLogsError') + ':', error)
        if (showLoading) {
          ElMessage.error(`${i18n_t('p2pSetting.fetchLogsError')}: ${error.message || i18n_t('p2pSetting.unknownError')}`)
        }
      } finally {
        if (showLoading) {
          logLoading.value = false
        }
      }
    }

    // 刷新日志
    const refreshLogs = () => {
      fetchLogs(true)
    }

    // 清空日志
    const clearLogs = async () => {
      try {
        await ElMessageBox.confirm(i18n_t('p2pSetting.confirmClearLogs'), i18n_t('p2pSetting.confirm'), {
          confirmButtonText: i18n_t('p2pSetting.confirm'),
          cancelButtonText: i18n_t('p2pSetting.cancelText'),
          type: 'warning'
        })

        logClearing.value = true

        const params = new URLSearchParams({
          module: 'p2p'
        })

        const response = await fetch(`${API_BASE_URL}/clear-moduleLog?${params.toString()}`, {
          method: 'DELETE',
          credentials: 'include'
        })

        if (!response.ok) throw new Error(i18n_t('p2pSetting.networkError'))

        const data = await response.json()

        if (data.errCode === 1000) {
          ElMessage.error(i18n_t('p2pSetting.pleaseLoginFirst'))
          router.push('/user-login?callback=p2p-setting')
          return
        }

        if (data.errCode !== 0) {
          throw new Error(data.errMsg || i18n_t('p2pSetting.clearLogsFailed'))
        }

        ElMessage.success(i18n_t('p2pSetting.logsCleared'))
        fetchLogs(true)
      } catch (error) {
        if (error !== 'cancel') {
          console.error(i18n_t('p2pSetting.clearLogsFailed') + ':', error)
          ElMessage.error(`${i18n_t('p2pSetting.clearLogsFailed')}: ${error.message || i18n_t('p2pSetting.unknownError')}`)
        }
      } finally {
        logClearing.value = false
      }
    }

    // 下载所有日志
    const downloadAllLogs = async () => {
      try {
        logDownloading.value = true

        // 获取所有日志（设置一个足够大的size值）
        const params = new URLSearchParams({
          module: 'p2p',
          page: 1,
          size: 10000 // 设置一个较大的数值，获取尽可能多的日志
        })

        const response = await fetch(`${API_BASE_URL}/get-moduleLog?${params.toString()}`, {
          credentials: 'include'
        })

        if (!response.ok) throw new Error(i18n_t('p2pSetting.networkError'))

        const data = await response.json()

        if (data.errCode === 1000) {
          ElMessage.error(i18n_t('p2pSetting.pleaseLoginFirst'))
          router.push('/user-login?callback=p2p-setting')
          return
        }

        if (data.errCode !== 0) {
          throw new Error(data.errMsg || i18n_t('p2pSetting.fetchLogsFailed'))
        }

        const allLogs = data.data || []

        if (allLogs.length === 0) {
          ElMessage.warning(i18n_t('p2pSetting.noLogsToDownload'))
          return
        }

        // 格式化所有日志内容
        let logContent = ''
        allLogs.forEach(log => {
          logContent += `[${log.Time}] [${log.Level}] ${log.Content}\n`
        })

        // 创建下载链接
        const blob = new Blob([logContent], { type: 'text/plain;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `p2p_logs_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.log`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)

        ElMessage.success(i18n_t('p2pSetting.downloadSuccess', { count: allLogs.length }))
      } catch (error) {
        console.error(i18n_t('p2pSetting.downloadFailed') + ':', error)
        ElMessage.error(`${i18n_t('p2pSetting.downloadFailed')}: ${error.message || i18n_t('p2pSetting.unknownError')}`)
      } finally {
        logDownloading.value = false
      }
    }

    // 处理每页条数变化
    const handleLogSizeChange = (size) => {
      logPageSize.value = size
      logCurrentPage.value = 1
      fetchLogs(true)
    }

    // 处理页码变化
    const handleLogPageChange = (page) => {
      logCurrentPage.value = page
      fetchLogs(true)
    }

    // 窗口大小变化处理
    const handleResize = () => {
      checkMobile()
    }

    // 初始化
    onMounted(() => {
      checkMobile()
      window.addEventListener('resize', handleResize)
      fetchP2PConfig()
    })

    onUnmounted(() => {
      window.removeEventListener('resize', handleResize)
      stopLogAutoRefresh()
    })
    
    return {
      isMobile,
      p2pConfig,
      p2pConfigForm,
      p2pServer,
      p2pServerForm,
      logDialogVisible,
      logLoading,
      logClearing,
      logDownloading,
      logsData,
      logTotal,
      logCurrentPage,
      logPageSize,
      logContentRef,
      autoRefreshEnabled,
      getStatusClass,
      updateP2PClientService,
      updateP2PServerService,
      stopP2PServerService,
      openP2PListStatus,
      showLogDialog,
      toggleAutoRefresh,
      refreshLogs,
      clearLogs,
      downloadAllLogs,
      handleLogSizeChange,
      handleLogPageChange
    }
  }
}
</script>

<style scoped lang="scss">
.p2p-settings-container {
  padding: 0px;
  max-width: 100%;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 0 12px;
    width: 100%;
  }
  
  @media (max-width: 480px) {
    padding: 0 8px;
  }
}

.settings-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  margin-bottom: 24px;
  overflow: hidden;
  width: 100%;
  box-sizing: border-box;
  
  .card-header {
    padding: 16px 20px;
    border-bottom: 1px solid #ebeef5;
    
    .card-header-content {
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-wrap: wrap;
      gap: 12px;
    }
    
    .card-title {
      font-size: 16px;
      font-weight: 600;
      color: #303133;
      margin: 0;
    }
  }
  
  .card-body {
    padding: 20px;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    
    .card-header {
      padding: 12px 16px;
      
      .card-header-content {
        flex-direction: column;
        align-items: flex-start;
      }
      
      .card-title {
        font-size: 15px;
      }
    }
    
    .card-body {
      padding: 12px;
    }
  }
  
  @media (max-width: 480px) {
    .card-header {
      padding: 10px 12px;
      
      .card-title {
        font-size: 14px;
      }
    }
    
    .card-body {
      padding: 8px;
    }
  }
}

.table-responsive {
  overflow-x: auto;
  width: 100%;
  
  @media (max-width: 768px) {
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
}

.settings-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  
  th, td {
    padding: 12px 15px;
    text-align: left;
    border-bottom: 1px solid #ebeef5;
  }
  
  th {
    background-color: #f5f7fa;
    color: #909399;
    font-weight: 600;
    white-space: nowrap;
  }
  
  td {
    color: #606266;
    vertical-align: middle;
    
    .el-input, .el-select {
      width: 100%;
      max-width: 120px;
    }
    
    .el-input {
      .el-input__inner {
        width: 100%;
      }
    }
    
    .el-select {
      width: 100%;
    }
  }
  
  .action-cell {
    white-space: nowrap;
    
    .action-buttons {
      display: flex;
      gap: 4px;
      flex-wrap: nowrap;
      
      .el-button {
        flex-shrink: 0;
      }
      
      .el-button + .el-button {
        margin-left: 0;
      }
    }
  }
}

.status {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  
  &.running {
    background-color: #f0f9eb;
    color: #67c23a;
  }
  
  &.stopped {
    background-color: #f5f5f5;
    color: #909399;
  }
  
  &.failed {
    background-color: #fef0f0;
    color: #f56c6c;
    font-weight: 500;
  }
}

/* 日志对话框样式 */
.log-dialog {
  :deep(.el-dialog__body) {
    padding: 20px;
    max-height: 70vh;
    overflow-y: auto;
  }
}

.log-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #ebeef5;
  
  .log-stats {
    display: flex;
    align-items: center;
    gap: 24px;
    font-size: 13px;
    color: #606266;
    
    .auto-refresh-control {
      display: flex;
      align-items: center;
      gap: 8px;
      padding-left: 16px;
      border-left: 1px solid #dcdfe6;
      
      .refresh-label {
        font-size: 12px;
        color: #909399;
      }
      
      .refresh-interval {
        font-size: 12px;
        color: #67c23a;
      }
    }
  }
  
  .log-actions {
    display: flex;
    gap: 8px;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
    
    .log-stats {
      width: 100%;
      flex-wrap: wrap;
      gap: 12px;
      
      .auto-refresh-control {
        padding-left: 0;
        border-left: none;
      }
    }
    
    .log-actions {
      width: 100%;
      justify-content: flex-end;
    }
  }
  
  @media (max-width: 480px) {
    .log-stats {
      font-size: 12px;
    }
  }
}

.log-content {
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background-color: #fafafa;
  margin-bottom: 16px;
  max-height: 400px;
  overflow-y: auto;
  
  .empty-log {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px;
    color: #909399;
    
    .el-icon {
      font-size: 32px;
      margin-bottom: 12px;
    }
    
    p {
      margin: 0;
      font-size: 14px;
    }
  }
  
  .log-list {
    padding: 12px;
    
    .log-item {
      padding: 8px 12px;
      border-bottom: 1px solid #e4e7ed;
      font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
      font-size: 13px;
      line-height: 1.5;
      display: flex;
      align-items: flex-start;
      gap: 8px;
      
      &:last-child {
        border-bottom: none;
      }
      
      &.log-level-error {
        background-color: #fef0f0;
      }
      
      &.log-level-warn {
        background-color: #fdf6ec;
      }
      
      .log-time {
        color: #909399;
        font-size: 12px;
        white-space: nowrap;
        flex-shrink: 0;
      }
      
      .log-level {
        font-weight: 600;
        font-size: 12px;
        white-space: nowrap;
        flex-shrink: 0;
        
        &.level-error {
          color: #f56c6c;
        }
        
        &.level-warn {
          color: #e6a23c;
        }
        
        &.level-info {
          color: #409eff;
        }
      }
      
      .log-message {
        color: #303133;
        word-break: break-all;
        flex: 1;
      }
    }
  }
  
  @media (max-width: 768px) {
    max-height: 350px;
    
    .log-list {
      padding: 8px;
      
      .log-item {
        font-size: 12px;
        flex-wrap: wrap;
        gap: 4px;
        
        .log-time {
          width: 100%;
          margin-bottom: 2px;
        }
        
        .log-message {
          width: 100%;
          margin-left: 0;
        }
      }
    }
  }
  
  @media (max-width: 480px) {
    max-height: 300px;
    
    .log-list {
      .log-item {
        font-size: 11px;
      }
    }
  }
}

.log-pagination {
  display: flex;
  justify-content: flex-end;
  
  @media (max-width: 768px) {
    justify-content: center;
  }
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  
  @media (max-width: 480px) {
    flex-direction: column;
    
    .el-button {
      width: 100%;
      margin-left: 0 !important;
    }
  }
}

/* 响应式处理 */
@media (max-width: 768px) {
  .settings-table {
    width: 100%;
    min-width: 100%;
    display: block;
    
    thead {
      display: none;
    }
    
    tbody, tr {
      width: 100%;
      display: block;
    }
    
    tr {
      margin-bottom: 16px;
      border: 1px solid #ebeef5;
      border-radius: 4px;
      background: #fff;
    }
    
    td {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 10px 12px;
      border-bottom: 1px solid #ebeef5;
      width: 100%;
      box-sizing: border-box;
      
      &:before {
        content: attr(data-label);
        font-weight: 600;
        color: #909399;
        margin-right: 12px;
        flex-shrink: 0;
        min-width: 80px;
        font-size: 13px;
      }
      
      &:last-child {
        border-bottom: none;
      }
      
      .el-input, .el-select {
        max-width: 150px;
        width: auto;
        flex: 1;
      }
      
      .el-input__inner {
        width: 100%;
      }
      
      &.action-cell {
        padding: 10px 12px;
        
        &:before {
          align-self: flex-start;
          padding-top: 4px;
        }
        
        .action-buttons {
          flex: 1;
          justify-content: flex-end;
        }
      }
      
      .status {
        padding: 2px 6px;
        font-size: 11px;
      }
    }
  }
}

@media (max-width: 480px) {
  .settings-table {
    td {
      padding: 8px 10px;
      font-size: 12px;
      
      &:before {
        min-width: 70px;
        font-size: 12px;
      }
      
      .el-input, .el-select {
        max-width: 130px;
      }
      
      &.action-cell {
        .action-buttons {
          .el-button--small {
            padding: 4px 6px;
            font-size: 11px;
          }
        }
      }
    }
  }
  
  .status {
    padding: 2px 4px;
    font-size: 10px;
  }
}

/* Element Plus 组件适配 */
:deep(.el-input__inner) {
  @media (max-width: 768px) {
    font-size: 13px;
    height: 32px;
    line-height: 32px;
  }
  
  @media (max-width: 480px) {
    font-size: 12px;
    height: 30px;
    line-height: 30px;
  }
}

:deep(.el-select) {
  @media (max-width: 768px) {
    .el-input__inner {
      height: 32px;
      line-height: 32px;
    }
  }
  
  @media (max-width: 480px) {
    .el-input__inner {
      height: 30px;
      line-height: 30px;
    }
  }
}

:deep(.el-button--small) {
  @media (max-width: 768px) {
    padding: 6px 12px;
    font-size: 12px;
  }
  
  @media (max-width: 480px) {
    padding: 5px 8px;
    font-size: 11px;
  }
}

:deep(.el-switch) {
  @media (max-width: 768px) {
    .el-switch__core {
      width: 36px !important;
      height: 18px !important;
      
      &:after {
        width: 14px !important;
        height: 14px !important;
      }
    }
  }
}
</style>